import { useMemo, useState } from "react";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { SubscriptionTable } from "../components/SubscriptionTable";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import type { Route } from "../routing";
import type { SubscriptionDTO, SubscriptionStatus } from "@shared/api";

type Filter = "all" | SubscriptionStatus;
type Sort = "name" | "renewal" | "seats";

interface Props {
  onOpenSubscription: (id: string) => void;
  onNav: (r: Route) => void;
  search: string;
}

export function Subscriptions({ onOpenSubscription, onNav, search }: Props) {
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("name");
  const { data: subs, loading, error } = useFetch(
    () => api.subscriptions.list(),
    [],
  );

  const filtered = useMemo<SubscriptionDTO[]>(() => {
    let list = subs ?? [];
    if (filter !== "all") list = list.filter((s) => s.status === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          s.domain.toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "renewal")
      sorted.sort(
        (a, b) =>
          new Date(a.termEnd).getTime() - new Date(b.termEnd).getTime(),
      );
    if (sort === "seats") sorted.sort((a, b) => b.seatsUsed - a.seatsUsed);
    return sorted;
  }, [subs, filter, sort, search]);

  const counts = useMemo(() => {
    const all = subs ?? [];
    return {
      all: all.length,
      active: all.filter((s) => s.status === "active").length,
      "renewal-due": all.filter((s) => s.status === "renewal-due").length,
      trial: all.filter((s) => s.status === "trial").length,
      paused: all.filter((s) => s.status === "paused").length,
    };
  }, [subs]);

  return (
    <div className="bbc-screen">
      <div className="bbc-screen-head">
        <div>
          <h1 className="bbc-h1">Subscriptions</h1>
          <p className="bbc-screen-sub">
            Each subscription is a licensed use case. Configure connected
            services, assign seats, and manage term length per subscription.
          </p>
        </div>
        <div className="bbc-screen-actions">
          <Button variant="secondary">Export portfolio</Button>
          <Button variant="primary" onClick={() => onNav("catalog")}>
            + New subscription
          </Button>
        </div>
      </div>

      <div className="bbc-toolbar">
        <div className="bbc-segmented">
          {(
            [
              { v: "all", l: `All · ${counts.all}` },
              { v: "active", l: `Active · ${counts.active}` },
              { v: "renewal-due", l: `Renewal due · ${counts["renewal-due"]}` },
              { v: "trial", l: `Trial · ${counts.trial}` },
              { v: "paused", l: `Paused · ${counts.paused}` },
            ] as Array<{ v: Filter; l: string }>
          ).map((o) => (
            <button
              key={o.v}
              type="button"
              className={filter === o.v ? "is-on" : ""}
              onClick={() => setFilter(o.v)}
            >
              {o.l}
            </button>
          ))}
        </div>
        <div className="bbc-toolbar-right">
          <label className="bbc-select-wrap">
            <span>Sort</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
            >
              <option value="name">Name</option>
              <option value="renewal">Renewal date</option>
              <option value="seats">Seats used</option>
            </select>
          </label>
        </div>
      </div>

      <Card padded={false}>
        {loading ? (
          <div className="bbc-screen-loading">Loading subscriptions…</div>
        ) : error ? (
          <div className="bbc-error">{error.message}</div>
        ) : (
          <>
            <SubscriptionTable
              subs={filtered}
              onOpen={onOpenSubscription}
            />
            {filtered.length === 0 && (
              <div className="bbc-empty">
                No subscriptions match those filters.
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
